import { resolveAction, type ActionResolution, type RuntimeActionRequest } from "../model/ActionResolver";
import { createInitialLevelState, type LevelState } from "../model/LevelState";
import type { DebugSnapshot } from "../types/debug";
import type { InputMapping } from "../types/action";
import type { LevelData } from "../types/level";
import type { SymbolDictionaryData } from "../types/symbol";

export type ControllerSignal =
  | { kind: "none" }
  | { kind: "restart" }
  | { kind: "toggleDebugFog" }
  | { kind: "openNotes" }
  | { kind: "returnToLevelSelect" };

export interface ControllerDispatchResult {
  readonly resolution: ActionResolution;
  readonly signal: ControllerSignal;
}

export interface ActionController {
  dispatch(request: RuntimeActionRequest, state: LevelState, level: LevelData): ControllerDispatchResult;
}

export interface ActionControllerOptions {
  readonly allowDebugActions?: boolean;
}

function buildSystemSnapshot(
  input: InputMapping,
  state: LevelState,
  valid: boolean,
  reason: string | null,
): DebugSnapshot {
  return {
    tick: state.tick,
    input,
    result: valid ? "valid" : "invalid",
    reason,
    state: {
      player: {
        x: state.player.x,
        y: state.player.y,
        facing: state.player.facing,
      },
      history: {
        length: state.history.length,
        last5: [...state.history.last5],
      },
      fog: {
        explored: state.fog.explored.map((point) => ({ x: point.x, y: point.y })),
        permanentlyRevealed: state.fog.permanentReveal,
        debugReveal: state.fog.debugReveal,
      },
      collectibles: {
        local: [...state.collectibles.local],
        permanent: [...state.collectibles.permanent],
      },
      breadcrumbs: {
        active: state.breadcrumbs.active.map((b) => ({ ...b })),
        local: [...state.breadcrumbs.local],
      },
      symbolSequence: {
        raw: [...state.symbolSequence.raw],
        expanded: [...state.symbolSequence.expanded],
        playerNotes: { ...state.symbolSequence.playerNotes },
      },
    },
  };
}

function wrapSystemResolution(
  request: RuntimeActionRequest,
  state: LevelState,
  valid: boolean = true,
  reason: string | null = null,
): ActionResolution {
  const input: InputMapping = {
    raw: request.rawInput,
    mapped: request.mappedInput,
  };
  const snapshot = buildSystemSnapshot(input, state, valid, reason);

  return {
    state: { ...state, lastDebugSnapshot: snapshot },
    snapshot,
    valid,
    reason,
  };
}

export class DefaultActionController implements ActionController {
  private readonly dictionary: SymbolDictionaryData;
  private readonly allowDebugActions: boolean;

  constructor(
    dictionary: SymbolDictionaryData,
    options: ActionControllerOptions = {},
  ) {
    this.dictionary = dictionary;
    this.allowDebugActions = options.allowDebugActions ?? false;
  }

  public dispatch(request: RuntimeActionRequest, state: LevelState, level: LevelData): ControllerDispatchResult {
    const mapped = request.mappedInput;

    switch (mapped) {
      case "restart": {
        const freshState = createInitialLevelState(level, this.dictionary, {
          playerNotes: state.symbolSequence.playerNotes,
          permanentReveal: state.fog.permanentReveal,
        });
        return {
          resolution: wrapSystemResolution(request, freshState),
          signal: { kind: "restart" },
        };
      }
      case "toggleDebugFog": {
        if (!this.allowDebugActions) {
          return {
            resolution: wrapSystemResolution(
              request,
              state,
              false,
              "Debug actions are disabled in this build.",
            ),
            signal: { kind: "none" },
          };
        }

        const nextState: LevelState = {
          ...state,
          fog: {
            ...state.fog,
            debugReveal: !state.fog.debugReveal,
          },
          tick: state.tick + 1,
        };
        return {
          resolution: wrapSystemResolution(request, nextState),
          signal: { kind: "toggleDebugFog" },
        };
      }
      case "openNotes":
        return {
          resolution: resolveAction(request, state, level),
          signal: { kind: "openNotes" },
        };
      case "returnToLevelSelect":
        return {
          resolution: resolveAction(request, state, level),
          signal: { kind: "returnToLevelSelect" },
        };
      default:
        return {
          resolution: resolveAction(request, state, level),
          signal: { kind: "none" },
        };
    }
  }
}
